import { useCallback, useState } from 'react';
import { SEARCH_V2_GROUP_PAGING } from '../constants/search.constants';

interface RevealedCountOptions {
  total: number;
  resetKey: unknown;
  initial?: number;
  step?: number;
}

export interface VisibleWindow {
  visibleCount: number;
  remaining: number;
  nextBatch: number;
}

/**
 * The part of a group a viewer sees: `visibleCount` rows of `total`, `remaining` rows
 * still hidden and `nextBatch` rows the next "Show more" would reveal.
 *
 * @param total - Rows in the group.
 * @param revealed - Rows revealed so far (may exceed `total`).
 * @param step - Rows one "Show more" reveals.
 * @returns The window.
 * @example
 * visibleWindow(12, 5, 10); // { visibleCount: 5, remaining: 7, nextBatch: 7 }
 */
export function visibleWindow(total: number, revealed: number, step: number): VisibleWindow {
  const visibleCount = Math.min(revealed, total);
  const remaining = total - visibleCount;
  return { visibleCount, remaining, nextBatch: Math.min(step, remaining) };
}

/**
 * Rows revealed after one "Show more": `step` more, never past `total`.
 *
 * @param revealed - Rows revealed so far.
 * @param total - Rows in the group.
 * @param step - Rows one "Show more" reveals.
 * @returns The new revealed count.
 * @example
 * revealMore(5, 12, 10); // 12
 */
export function revealMore(revealed: number, total: number, step: number): number {
  return Math.min(revealed + step, total);
}

/**
 * How many rows of a search-v2 group to render: `initial` on arrival, `step` more per
 * `showMore`, never past `total`. Resets to `initial` when `resetKey` changes, so a new
 * search starts every group over. No DOM, so web and mobile share it.
 *
 * @param options - `total` rows in the group, `resetKey` that changes with the search
 *   (the results object), `initial` rows on arrival and `step` rows per `showMore`, both
 *   defaulting to `SEARCH_V2_GROUP_PAGING`.
 * @returns `visibleCount` rows to render, `remaining` rows hidden, `nextBatch` rows the next
 *   `showMore` reveals, and `showMore`.
 * @example
 * const { visibleCount, remaining, nextBatch, showMore } = useRevealedCount({
 *   total: results.length,
 *   resetKey: results,
 * });
 * {results.slice(0, visibleCount).map(renderRow)}
 * {remaining > 0 && <button onClick={showMore}>Show {nextBatch} more</button>}
 */
export function useRevealedCount({
  total,
  resetKey,
  initial = SEARCH_V2_GROUP_PAGING.INITIAL,
  step = SEARCH_V2_GROUP_PAGING.STEP,
}: RevealedCountOptions) {
  const [revealed, setRevealed] = useState(initial);
  const [appliedKey, setAppliedKey] = useState(resetKey);

  // Reset during render, so the first paint of a new search is already `initial` rows.
  if (appliedKey !== resetKey) {
    setAppliedKey(resetKey);
    setRevealed(initial);
  }

  const showMore = useCallback(
    () => setRevealed(current => revealMore(current, total, step)),
    [total, step],
  );

  return { ...visibleWindow(total, revealed, step), showMore };
}
